/**
 * @file MyBenefitsList.js
 * @description Landing page for the Benefits module. The user picks a date (defaulting to today); the page resolves the active plan year from that date and lists the user's enrollments for that year as an image-first card grid, alongside the plan-year welcome message and any plan-year-level assets.
 */

import { useState, useEffect, useMemo, useCallback } from 'react';

import styles from './MyBenefitsList.module.scss';
import Spinner from '../ui/spinners/Spinner';
import Toast from '../ui/toasts/Toast';
import CustomDatePicker from '../helpers/CustomDatePicker_V2';
import { useAuthContext } from '../../store/Contexts';
import { BenefitsViewModeEnum, ClientOptionEnum } from '../../utils/data/enums';
import {
    readBoolOption,
    readStringOption,
    hasTextContent,
    getDefaultBenefitTextHtml,
} from '../../utils/utils';

import {
    fetchPlanYearById,
    searchPlanYears,
} from '../../services/planYearService';
import { fetchAssetByID, getClientOptions } from '../../services/clientService';
import {
    searchBenefitPlans,
    searchPlanTypes,
} from '../../services/benefitPlanService';
import {
    getIndividualEnrollment,
    getIndividualRecord,
    getUserPreferences,
    updateCommunicationPreferences,
} from '../../services/individualService';
import { fetchMemberOf } from '../../services/populationGroupService';
import { logEvent } from '../../services/loggingService';
import { EventDataTypeEnum, LogEventType } from '../../utils/data/enums';
import WelcomeBlock from './_components/WelcomeBlock';
import AssetsRow from './_components/AssetsRow';
import BenefitGrid from './_components/BenefitGrid';
import BenefitList from './_components/BenefitList';
import ViewModeToggle from '../ui/view_mode_toggle/ViewModeToggle';
import RichTextContent from '../ui/RichTextContent';
import { parseLocalDate as parseDate } from './_helpers/dateHelpers';
import {
    buildNoEnrollmentPlaceholders,
    collectHiddenPlanIds,
    filterVisibleEnrollments,
} from './benefitVisibility';
import { getText } from '../../i18n';

const INFINITE_END = '9999-12-31';

function todayIso() {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
}

function planYearForDate(planYears, isoDate) {
    if (!isoDate) {
        return null;
    }
    const target = parseDate(isoDate);
    return planYears.find(function matches(py) {
        const start = parseDate(py.startDate);
        const end = parseDate(py.endDate);
        return start <= target && target <= end;
    });
}

function relevantPlanYears(planYears, hireDate) {
    if (!hireDate) {
        return planYears;
    }
    return planYears.filter(function afterHire(py) {
        return parseDate(py.endDate) >= hireDate;
    });
}

function readBenefitsViewModeOption(options) {
    const value = readStringOption(
        options,
        ClientOptionEnum.IndividualBenefitsView,
    );
    const parsed = Number(value);
    if (
        parsed === BenefitsViewModeEnum.Grid ||
        parsed === BenefitsViewModeEnum.List
    ) {
        return parsed;
    }
    return BenefitsViewModeEnum.Grid;
}

function resolveInitialViewMode(preferences, defaultViewMode) {
    const preferenceViewMode = Number(preferences?.benefitsViewMode);
    if (
        preferenceViewMode === BenefitsViewModeEnum.Grid ||
        preferenceViewMode === BenefitsViewModeEnum.List
    ) {
        return preferenceViewMode;
    }
    return defaultViewMode;
}

export default function MyBenefitsList() {
    const { user } = useAuthContext();
    const userId = user?.jwt?.ind;
    const tenantUserId = user?.id;

    const [isLoading, setIsLoading] = useState(true);
    const [planYears, setPlanYears] = useState([]);
    const [enrollments, setEnrollments] = useState([]);
    const [options, setOptions] = useState([]);
    const [selectedDate, setSelectedDate] = useState(todayIso());
    const [planYearDetails, setPlanYearDetails] = useState(null);
    const [planYearAssetMap, setPlanYearAssetMap] = useState({});
    const [planTypes, setPlanTypes] = useState(null);
    const [memberOf, setMemberOf] = useState([]);
    const [hiddenPlanIds, setHiddenPlanIds] = useState(new Set());
    const [isPlanYearContextLoading, setIsPlanYearContextLoading] =
        useState(false);
    const [userPreferences, setUserPreferences] = useState(null);
    const [viewMode, setViewMode] = useState(BenefitsViewModeEnum.Grid);

    useEffect(
        function loadInitialData() {
            if (!userId) {
                return undefined;
            }
            let cancelled = false;
            async function loadAll() {
                setIsLoading(true);
                try {
                    const [
                        individualRes,
                        planYearsRes,
                        enrollmentsRes,
                        optionsRes,
                        memberOfRes,
                        preferencesRes,
                        plansRes,
                    ] = await Promise.all([
                        getIndividualRecord(userId),
                        searchPlanYears(null, 1, 20, '', 'name', 'asc', true),
                        getIndividualEnrollment(userId, {}),
                        getClientOptions(0, [
                            ClientOptionEnum.DisplayBenefitText,
                            ClientOptionEnum.BenefitText,
                            ClientOptionEnum.DisplayEmployeeCosts,
                            ClientOptionEnum.DisplayEmployerCosts,
                            ClientOptionEnum.RenameBenefitsMenu,
                            ClientOptionEnum.IndividualBenefitsView,
                        ]),
                        fetchMemberOf(userId),
                        getUserPreferences(userId, tenantUserId).catch(
                            function onPreferencesError() {
                                return null;
                            },
                        ),
                        searchBenefitPlans(null, 1, 1000).catch(
                            function onPlansError() {
                                return null;
                            },
                        ),
                    ]);
                    if (cancelled) {
                        return;
                    }

                    const hireDate = individualRes?.hireDate
                        ? new Date(individualRes.hireDate)
                        : null;

                    setPlanYears(
                        relevantPlanYears(planYearsRes.viewModels, hireDate),
                    );
                    setEnrollments(enrollmentsRes);
                    setOptions(optionsRes);
                    setMemberOf(memberOfRes);
                    setHiddenPlanIds(
                        collectHiddenPlanIds(plansRes?.viewModels ?? []),
                    );
                    setUserPreferences(preferencesRes);
                    const defaultViewMode =
                        readBenefitsViewModeOption(optionsRes);
                    setViewMode(
                        resolveInitialViewMode(preferencesRes, defaultViewMode),
                    );
                } catch (err) {
                    console.error('Failed loading benefits page data', err);
                    Toast.error(getText('msg_3fc006170cee'));
                } finally {
                    if (!cancelled) {
                        setIsLoading(false);
                    }
                }
            }
            loadAll();
            return function cleanup() {
                cancelled = true;
            };
        },
        [userId, tenantUserId],
    );

    const handleViewModeChange = useCallback(
        function onViewModeChange(newViewMode) {
            setViewMode(newViewMode);
            if (!userId || !tenantUserId) {
                return;
            }
            updateCommunicationPreferences(userId, tenantUserId, {
                ...userPreferences,
                benefitsViewMode: newViewMode,
            })
                .then(function onSaved(updatedPreferences) {
                    setUserPreferences(updatedPreferences);
                })
                .catch(function onSaveError() {
                    Toast.error(getText('msg_607d98f2a881'));
                });
        },
        [userId, tenantUserId, userPreferences],
    );

    const selectedPlanYear = useMemo(
        function resolvePlanYear() {
            return planYearForDate(planYears, selectedDate);
        },
        [planYears, selectedDate],
    );
    const selectedPlanYearId = selectedPlanYear?.id ?? '';
    const mainPlanYearContextName = `Main - ${
        selectedPlanYear?.name || 'Unknown plan year'
    }`;
    const displayBenefitText = readBoolOption(
        options,
        ClientOptionEnum.DisplayBenefitText,
    );
    const benefitText = readStringOption(options, ClientOptionEnum.BenefitText);
    const renameBenefitsMenu = readStringOption(
        options,
        ClientOptionEnum.RenameBenefitsMenu,
    );
    const benefitsMenuLabel = renameBenefitsMenu || 'Benefits';
    const handlePlanYearAssetOpen = useCallback(
        function onPlanYearAssetOpen(asset) {
            void logEvent(
                LogEventType.UserDocumentViewed,
                `Plan year asset clicked: ${asset?.name || 'Untitled asset'}`,
                {
                    subjectDataTypeId: EventDataTypeEnum.Asset,
                    subjectId: asset?.id ?? null,
                    subjectName: asset?.name || 'Untitled asset',
                    contextDataTypeId: EventDataTypeEnum.Benefits,
                    contextId: selectedPlanYearId,
                    contextName: mainPlanYearContextName,
                },
            );
        },
        [mainPlanYearContextName, selectedPlanYearId],
    );

    useEffect(
        function loadPlanYearDetails() {
            if (!selectedPlanYearId) {
                setPlanYearDetails(null);
                setPlanYearAssetMap({});
                setPlanTypes([]);
                setIsPlanYearContextLoading(false);
                return undefined;
            }
            let cancelled = false;
            async function load() {
                setPlanTypes(null);
                setIsPlanYearContextLoading(true);
                try {
                    const [py, planTypeRows] = await Promise.all([
                        fetchPlanYearById(selectedPlanYearId, true),
                        searchPlanTypes(null, selectedPlanYearId),
                    ]);
                    if (cancelled) {
                        return;
                    }
                    setPlanYearDetails(py);
                    setPlanTypes(planTypeRows);

                    const assets = py?.planYearAssets ?? [];
                    if (assets.length === 0) {
                        setPlanYearAssetMap({});
                        return;
                    }
                    const fetched = await Promise.all(
                        assets.map(function loadAsset(asset) {
                            return fetchAssetByID(asset.assetId).catch(
                                function onError() {
                                    return null;
                                },
                            );
                        }),
                    );
                    if (cancelled) {
                        return;
                    }
                    const map = {};
                    fetched.forEach(function index(detail) {
                        if (detail?.id) {
                            map[detail.id] = detail;
                        }
                    });
                    setPlanYearAssetMap(map);
                } catch (err) {
                    console.error('Failed loading plan year details', err);
                    Toast.error(getText('msg_332f920d3418'));
                } finally {
                    if (!cancelled) {
                        setIsPlanYearContextLoading(false);
                    }
                }
            }
            load();
            return function cleanup() {
                cancelled = true;
            };
        },
        [selectedPlanYearId],
    );

    const handleDateChange = useCallback(function onDateChange(value) {
        if (value) {
            setSelectedDate(value);
        }
    }, []);

    const filteredEnrollments = useMemo(
        function filter() {
            //Remove these block as enrollments should be able to exist/display in the list without a matching plan year
            /*if (!selectedPlanYear) {
                return [];
            }*/
            if (planTypes == null) {
                return [];
            }

            const infinite = parseDate(INFINITE_END).getTime();

            const selected = parseDate(selectedDate);

            const dateFiltered = enrollments.filter(function activeOnDate(e) {
                const eStart = parseDate(e.startDate);
                const eEnd = parseDate(e.endDate);

                return (
                    eStart <= selected &&
                    (eEnd >= selected || eEnd.getTime() === infinite)
                );
            });

            const visibilityFiltered = filterVisibleEnrollments(
                dateFiltered,
                planTypes,
                memberOf,
            );

            const placeholders = buildNoEnrollmentPlaceholders(
                dateFiltered,
                planTypes,
                enrollments,
                memberOf,
                hiddenPlanIds,
            );

            return [...visibilityFiltered, ...placeholders].sort(
                function bySortOrder(a, b) {
                    return a.planTypeSortOrder - b.planTypeSortOrder;
                },
            );
        },
        [
            enrollments,
            memberOf,
            planTypes,
            selectedPlanYear,
            selectedDate,
            hiddenPlanIds,
        ],
    );

    const displayEmployeeCosts = readBoolOption(
        options,
        ClientOptionEnum.DisplayEmployeeCosts,
    );
    const displayEmployerCosts = readBoolOption(
        options,
        ClientOptionEnum.DisplayEmployerCosts,
    );

    if (isLoading || isPlanYearContextLoading) {
        return <Spinner width={50} className="d-block mx-auto mt-5 pt-5" />;
    }

    return (
        <div className="page-content">
            <div className={`ing-card ${styles.card}`}>
                <div className="p-4 p-md-5">
                    <header className="mb-4">
                        <h1 className="ing-text-heading3 mb-1">
                            {benefitsMenuLabel}
                        </h1>
                        {displayBenefitText && hasTextContent(benefitText) ? (
                            <div
                                className={`ing-text-heading5 mb-0 ${styles.headerSubtitle}`}
                            >
                                <RichTextContent content={benefitText} />
                            </div>
                        ) : (
                            <div
                                className={`ing-text-heading5 mb-0 ${styles.headerSubtitle}`}
                            >
                                <RichTextContent
                                    content={getDefaultBenefitTextHtml()}
                                />
                            </div>
                        )}
                    </header>

                    <div className="d-flex flex-wrap align-items-center gap-3 mb-4">
                        <div className={styles.datePickerWrap}>
                            <CustomDatePicker
                                selectedDate={selectedDate}
                                onDateChange={handleDateChange}
                                hideErrorMessage
                            />
                        </div>
                    </div>

                    <div className="mb-4">
                        <WelcomeBlock
                            planYearText={planYearDetails?.text}
                            fallbackText={benefitText}
                            enabled={displayBenefitText}
                        />
                    </div>

                    {planYearDetails?.planYearAssets?.length > 0 && (
                        <section className="mt-5">
                            <h2
                                className={`ing-text-heading4 mb-3 ${styles.sectionTitle}`}
                            >
                                {getText('msg_03e7516d5c17')}
                            </h2>
                            <AssetsRow
                                assets={planYearDetails.planYearAssets}
                                assetDetailsMap={planYearAssetMap}
                                onAssetOpen={handlePlanYearAssetOpen}
                                planId={selectedPlanYearId}
                                contextName={mainPlanYearContextName}
                            />
                        </section>
                    )}

                    <section className="mt-5">
                        <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-4">
                            <div>
                                <h2
                                    className={`ing-text-heading4 mb-2 ${styles.sectionTitle}`}
                                >
                                    {getText('msg_e2d135ad2078')}
                                </h2>
                                <p
                                    className={`ing-text-heading5 mb-0 ${styles.sectionSubtitle}`}
                                >
                                    {getText('msg_c56d74e0b37a')}
                                </p>
                            </div>
                            <ViewModeToggle
                                selectedViewMode={viewMode}
                                onChange={handleViewModeChange}
                            />
                        </div>
                        {viewMode === BenefitsViewModeEnum.List ? (
                            <BenefitList
                                enrollments={filteredEnrollments}
                                planYearId={selectedPlanYearId}
                                displayEmployeeCosts={displayEmployeeCosts}
                                displayEmployerCosts={displayEmployerCosts}
                            />
                        ) : (
                            <BenefitGrid
                                enrollments={filteredEnrollments}
                                planYearId={selectedPlanYearId}
                            />
                        )}
                    </section>
                </div>
            </div>
        </div>
    );
}
